import {
  ApplicationRef,
  Component,
  ElementRef,
  Input,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { TextPipe, TextService } from '@ps20-frontend/text-service';
import {
  NxDialogService,
  NxModalRef,
  NxModalContentDirective,
  NxModalActionsDirective,
} from '@allianz/ng-aquila/modal';
import { NxtInfoIconModule } from '@nxt/ngx-ndbx-extensions/info-icon';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { NxButtonComponent } from '@allianz/ng-aquila/button';
import { NxHeadlineComponent } from '@allianz/ng-aquila/headline';
import { NxCopytextComponent } from '@allianz/ng-aquila/copytext';

export const InfoIconComponentTextKeys = {
  closeButton: 'buttons.close',
};

const DEFAULT_ARIA_LABEL_PREFIX = 'Info-Icon für ';
export const POPOVER_MAX_CONTENT_LENGTH_THRESHOLD = 400;
const DEFAULT_BUTTON_TEXT = 'Schließen';

export function resolveInfoIconAriaLabelPrefix(textService: TextService): string {
  return textService.get('infoIconAriaLabelPrefix', false) || DEFAULT_ARIA_LABEL_PREFIX;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'info-icon',
  templateUrl: './info-icon.component.html',
  styleUrls: ['./info-icon.component.scss'],
  imports: [
    NxtInfoIconModule,
    NgClass,
    NgTemplateOutlet,
    NxModalContentDirective,
    NxModalActionsDirective,
    NxButtonComponent,
    NxHeadlineComponent,
    NxCopytextComponent,
    TextPipe,
  ],
})
export class InfoIconComponent {
  private readonly textService = inject(TextService);
  private readonly appRef = inject(ApplicationRef);
  dialogService = inject(NxDialogService);
  private readonly elementRef = inject(ElementRef);

  @Input() textKey = '';
  @Input() textContent = '';
  @Input() invertColor = false;
  @Input() ariaLabel = '';
  @Input() ariaLabelledBy?: string;
  @Input() closeBtnText: string = DEFAULT_BUTTON_TEXT;
  @Input() margin = true;
  @Input() forceModal = false;
  @Input() modalTitle = '';

  textKeys = InfoIconComponentTextKeys;
  private readonly textTransformer: TextPipe;
  ariaLabelPrefix = '';

  @ViewChild('modalTemplate') templateRef: TemplateRef<unknown>;
  templateDialogRef: NxModalRef<unknown>;

  constructor() {
    this.textTransformer = new TextPipe(this.textService);
    this.ariaLabelPrefix = resolveInfoIconAriaLabelPrefix(this.textService);
  }

  get fullAriaLabel(): string {
    const raw = this.ariaLabelPrefix + this.ariaLabel;
    const withoutEntities = raw.replaceAll(/&shy;/g, '').replaceAll(/&nbsp;/g, ' ');
    let stripped = withoutEntities;
    let previous: string;
    do {
      previous = stripped;
      stripped = stripped.replace(/<[^>]*>/g, '');
    } while (stripped !== previous);
    return stripped;
  }

  openFromTemplate(): void {
    if (this.templateDialogRef) {
      return;
    }
    this.templateDialogRef = this.dialogService.open(this.templateRef, {
      minWidth: '400px',
      maxWidth: '580px',
      showCloseIcon: true,
      closeIconButtonLabel: 'Dialog schließen',
    });
    this.templateDialogRef.afterClosed().subscribe(() => {
      this.templateDialogRef = null;
    });
  }

  shouldRenderPopover(textServiceResult: string): boolean {
    return !this.forceModal && this.contentLengthIsBelowPopoverThreshold(textServiceResult);
  }

  shouldRenderComponent(textServiceResult: string): boolean {
    if (this.textContent) {
      return true;
    }
    return textServiceResult && textServiceResult !== this.textKey;
  }

  contentLengthIsBelowPopoverThreshold(textServiceResult: string): boolean {
    const textContent = this.textContent || textServiceResult;
    return textContent.length < POPOVER_MAX_CONTENT_LENGTH_THRESHOLD;
  }

  closeModal(): void {
    this.templateDialogRef.close();
    this.appRef.tick();
  }
}
